
import { Platform } from 'react-native';
import * as WebBrowser from 'expo-web-browser';
import * as utils from '@lib/utils';
import * as service from '@lib/service';

import  { PurchasesPackage } from 'react-native-purchases';
import { Package } from '@revenuecat/purchases-js'
import { AppAlertType } from '@lib/hooks/base/useAppAlerts';

const Purchases = Platform.select({
  // ANDROID
  native: require('react-native-purchases').default,
  // WEB
  default: require('@revenuecat/purchases-js').Purchases,
});

const revenueCatWebBillingApiKey = process.env.EXPO_PUBLIC_PAYBOOKS_REVENUECAT_CLIENT_WEB_APP_API_KEY; 
const androidAppApiKey =  process.env.EXPO_PUBLIC_PAYBOOKS_REVENUECAT_CLIENT_ANDROID_APP_API_KEY; 
 
if (Platform.OS === 'web' && !revenueCatWebBillingApiKey) { 
  console.error("RevenueCat web API key is missing. Please check your .env file for EXPO_PUBLIC_PAYBOOKS_REVENUECAT_CLIENT_WEB_APP_API_KEY."); 
} 
 
if (Platform.OS === 'android' && !androidAppApiKey) { 
  console.error("RevenueCat Android API key is missing. Please check your .env file for EXPO_PUBLIC_PAYBOOKS_REVENUECAT_CLIENT_ANDROID_APP_API_KEY."); 
}

export interface SubscriptionPackage {
  identifier: string;
  name: string;
  description: string;
  purchase: (alert: AppAlertType) => Promise<any>;
}

export interface ActiveSubscription {
  expirationDate: Date;
  purchaseDate: Date;
  monthlyPeriods: number;
  productId: string;
}

export default class RevenueCatService {
  static purchases = null;

  static async initialize(appUserID : string) {
    // PLATFORM: WEB
    if (Platform.OS === 'web') {
      RevenueCatService.purchases = Purchases.configure(revenueCatWebBillingApiKey, appUserID);
    }
    // PLATFORM: ANDROID
    else {
      RevenueCatService.purchases = Purchases.configure({ apiKey: androidAppApiKey, appUserID });
      // /* const restore = */ await Purchases.restorePurchases();
    }
    return RevenueCatService.purchases;
  }

  static async getCustomerInfo() {
    let customerInfo = null;
    // PLATFORM: WEB
    if (Platform.OS === 'web') {
      customerInfo = await (RevenueCatService?.purchases as typeof Purchases)?.getCustomerInfo();
    }
    // PLATFORM: ANDROID
    else {
      customerInfo = await Purchases.getCustomerInfo();
    }
    return customerInfo;
  }

  static async getSubscriptionPackages(): Promise<SubscriptionPackage[]> {
    let packages = null;
    // PLATFORM: WEB
    if (Platform.OS === 'web') {
      const offerings = await (RevenueCatService?.purchases as typeof Purchases)?.getOfferings();
      packages = offerings?.current?.availablePackages?.map( (p :  ( /* PurchasesPackage */ Package)) => {
        return {
          identifier : p.webBillingProduct.identifier,
          name : p.webBillingProduct.title,
          description : p.webBillingProduct.description,
          purchase: (alert: AppAlertType) => RevenueCatService.purchasePackage(p, alert),
        };
      });
    }
    // PLATFORM: ANDROID
    else {
      const offerings = await Purchases.getOfferings();
      packages = offerings?.current?.availablePackages?.map( (p :  (PurchasesPackage)) => {
        return {
          identifier : p.identifier,
          name : p.product.title.replace(/\s*\(.*?\)\s*/g, '').trim(),
          description : p.product.description, 
          purchase: (alert: AppAlertType) => RevenueCatService.purchasePackage(p, alert),
        };
      });
    }

    return packages;
  }

  static async purchasePackage(purchasePackage : any, alert: AppAlertType) {
    // Cross platform message upon updating subscription
    const successMsg = "Please reload the app upon accessing the subscription paywall.";
    const errorMsg = "There was an error while trying to create a purchase session.";

    // PLATFORM: WEB
    if (Platform.OS === 'web') {
      const axiosService = await service.getInstance();
      // Create Stripe checkout session
      const seesion =  await axiosService.post('subscriptions/checkout-with-stripe', { data: purchasePackage });
      // Open Stripe checkout session page
      const url = seesion?.data?.url;
      if (url) {
        // Open Stripe checkout session separately in the browser
        await WebBrowser.openAuthSessionAsync(url);
      } else {
        alert('Error', errorMsg);
      }
    }
    // PLATFORM: ANDROID
    else {
      await Purchases.purchasePackage(purchasePackage);
    }

    alert('Success', successMsg, [{ text: 'OK', onPress: utils.reloadApp }]);
  }

  static async getActiveSubscriptions(): Promise<ActiveSubscription[]> {
    const customerInfo = await RevenueCatService?.getCustomerInfo();
    
    let expirationDates = null;
    let purchaseDates = null;
    const activeSubscriptions: ActiveSubscription[] = [];

    if (Platform.OS === 'web') {
      expirationDates = customerInfo.allExpirationDatesByProduct;
      purchaseDates = customerInfo.allPurchaseDatesByProduct;
    } else { // Native (Android/iOS)
      expirationDates = customerInfo.allExpirationDates;
      purchaseDates = customerInfo.allPurchaseDates;
    }

    for (const productId in expirationDates) {
      const expirationDate = new Date(expirationDates[productId]);
      const purchaseDate = new Date(purchaseDates[productId]);
      const monthlyPeriods = Math.abs(Math.floor(utils.differenceInDays(purchaseDate, expirationDate) / 30));

      if (expirationDate > new Date()) {
        activeSubscriptions.push({ expirationDate, purchaseDate, monthlyPeriods, productId });
      }
    }

    return activeSubscriptions;
  }

  static async manageSubscription(manageLink: string) {
      if (manageLink) {
        await WebBrowser.openBrowserAsync(manageLink);
      } else {
         throw "NO MANAGEMENT URL";
      }
  }

}
